/**
 * Cohort Management API
 */

import { apiClient, ApiResponse, ApiError, ApiErrorResponse } from './client';

export type CohortVisibility = 'public' | 'org-restricted';

export interface Cohort {
  cohortId: string;
  programId: string;
  programName?: string; // Populated by backend from Program table
  cohortName: string;
  description: string;
  startDate: string;
  endDate: string;
  registrationStartDate?: string;
  registrationEndDate?: string;
  status: 'upcoming' | 'registration-open' | 'active' | 'completed';
  capacity: number;
  enrollmentCount?: number;
  graduatedCount?: number;
  visibility: CohortVisibility;
  orgLeader?: string;
  orgLeaderName?: string;
  wikiUrl?: string;
  slackChannel?: string;
  selfEnrollment: boolean;
  mentorsEnabled: boolean;
  graduationApproval: 'automatic' | 'admin';
  isPublished: boolean;
  isOpen: boolean;
  // Caller-specific eligibility flags computed server-side
  canEnroll?: boolean;
  canRequestMentor?: boolean;
}

export interface CohortActionItems {
  role: 'admin' | 'mentor' | 'participant' | 'none';
  // Admin
  pendingMentorRequests?: number;
  pendingGraduations?: number;
  unassignedParticipants?: number;
  // Mentor
  pendingActivityReviews?: number;
  menteeCount?: number;
  // Participant
  pendingApprovals?: number;
  overdueActivities?: number;
  dueThisWeekActivities?: number;
}

export interface CreateCohortRequest {
  programId: string;
  cohortName: string;
  description: string;
  startDate: string;
  endDate: string;
  registrationOpenDate?: string;
  registrationDeadline?: string;
  wikiUrl?: string;
  slackChannel?: string;
  amazonLearnUrl?: string;
  capacity?: number;
  visibility: CohortVisibility;
  orgLeader?: string;
  orgLeaderName?: string;
  selfEnrollment: boolean;
  mentorsEnabled?: boolean;
  graduationApproval?: 'automatic' | 'admin';
  isPublished?: boolean;
  isOpen?: boolean;
}

export interface UpdateCohortRequest {
  programId?: string;
  cohortName?: string;
  description?: string;
  wikiUrl?: string | null;
  slackChannel?: string | null;
  startDate?: string;
  endDate?: string;
  registrationOpenDate?: string;
  registrationDeadline?: string;
  capacity?: number | null;
  visibility?: CohortVisibility;
  orgLeader?: string | null;
  orgLeaderName?: string | null;
  selfEnrollment?: boolean;
  mentorsEnabled?: boolean;
  graduationApproval?: 'automatic' | 'admin';
  isPublished?: boolean;
  isOpen?: boolean;
}

export interface GraduationRequest {
  userId: string;
  userName: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: number;
  reviewedBy?: string;
  reviewedAt?: number;
  notes?: string;
}

export interface GraduationRequestsResponse {
  cohortId: string;
  requests: GraduationRequest[];
  pendingCount: number;
}

export interface Enrollment {
  cohortId: string;
  userId: string;
  userName: string;
  userEmail: string;
  status: 'enrolled' | 'withdrawn' | 'graduated';
  enrolledAt: string;
  withdrawnAt?: string;
  graduatedAt?: string;
  completionPercentage?: number;
}

export interface CohortParticipant {
  userId: string;
  userName: string;
  userEmail: string;
  enrollmentStatus: 'enrolled' | 'withdrawn' | 'graduated';
  enrolledAt: string;
  isMentor: boolean;
  mentorId?: string;
  mentorName?: string;
  completionPercentage?: number;
}

export interface CohortParticipantsResponse {
  cohortId: string;
  participantCount: number;
  mentorCount: number;
  participants: CohortParticipant[];
}

/**
 * Cohort API Service
 */
export const cohortsApi = {
  /**
   * Create a new cohort
   */
  async create(data: CreateCohortRequest): Promise<Cohort> {
    const response = await apiClient.post<ApiResponse<{ message: string; cohort: Cohort }>>('/cohorts', data);
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data.cohort;
  },

  /**
   * Get cohort by ID
   */
  async get(cohortId: string): Promise<Cohort> {
    const response = await apiClient.get<ApiResponse<Cohort>>(`/cohorts/${cohortId}`);
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Update cohort
   */
  async update(cohortId: string, data: UpdateCohortRequest): Promise<Cohort> {
    const response = await apiClient.put<ApiResponse<Cohort>>(`/cohorts/${cohortId}`, data);
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Delete cohort
   */
  async delete(cohortId: string, programId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/cohorts/${cohortId}?programId=${encodeURIComponent(programId)}`);
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * List all cohorts (with optional program filter)
   */
  async list(programId?: string): Promise<Cohort[]> {
    const path = programId ? `/cohorts?programId=${programId}` : '/cohorts';
    const response = await apiClient.get<ApiResponse<Cohort[]>>(path);
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Enroll user in cohort
   */
  async enrollUser(cohortId: string, userId: string, programId: string): Promise<void> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/cohorts/${cohortId}/enrollments`,
      { userId, programId }
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Unenroll user from cohort (soft withdraw)
   */
  async unenrollUser(cohortId: string, userId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/cohorts/${cohortId}/enrollments/${userId}`
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Hard-delete a single enrollment record (admin only)
   */
  async deleteEnrollment(cohortId: string, userId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/cohorts/${cohortId}/enrollments/${userId}?force=true`
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Hard-delete multiple enrollment records (admin only).
   * Uses POST /enrollments/bulk-delete to send a body with the list of userIds.
   */
  async bulkDeleteEnrollments(cohortId: string, userIds: string[]): Promise<{ deleted: string[]; failed: { userId: string; error: string }[] }> {
    const response = await apiClient.post<ApiResponse<{ message: string; results: { deleted: string[]; failed: { userId: string; error: string }[] } }>>(
      '/enrollments/bulk-delete',
      { cohortId, userIds }
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data.results;
  },

  /**
   * List cohort enrollments
   */
  async listEnrollments(cohortId: string): Promise<Enrollment[]> {
    const response = await apiClient.get<ApiResponse<Enrollment[]>>(
      `/cohorts/${cohortId}/enrollments`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Get cohort participants with all relationships (optimized)
   * Returns enrollments, mentors, and mentor-mentee relationships in one call
   */
  async getParticipants(cohortId: string, statusFilter?: string): Promise<CohortParticipantsResponse> {
    const url = statusFilter 
      ? `/cohorts/${cohortId}/participants?status=${statusFilter}`
      : `/cohorts/${cohortId}/participants`;
    const response = await apiClient.get<ApiResponse<CohortParticipantsResponse>>(url);
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Bulk enroll users
   */
  async bulkEnroll(cohortId: string, users: { userId: string; userName: string; userEmail: string }[]): Promise<{ message: string; results: { success: string[]; failed: { userId: string; error: string }[] } }> {
    const response = await apiClient.post<ApiResponse<{ message: string; results: { success: string[]; failed: { userId: string; error: string }[] } }>>(
      '/enrollments/bulk',
      { cohortId, users }
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Assign mentor
   */
  async assignMentor(cohortId: string, mentorId: string, menteeId: string): Promise<void> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/cohorts/${cohortId}/mentors`,
      { mentorId, menteeId }
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Request graduation from a cohort (participant)
   */
  async requestGraduation(cohortId: string): Promise<{ graduated: boolean; message: string }> {
    const response = await apiClient.post<ApiResponse<{ graduated: boolean; message: string }>>(
      `/cohorts/${cohortId}/graduation`,
      {}
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },

  /**
   * List graduation requests for a cohort (admin)
   */
  async listGraduationRequests(cohortId: string): Promise<GraduationRequestsResponse> {
    const response = await apiClient.get<ApiResponse<GraduationRequestsResponse>>(
      `/cohorts/${cohortId}/graduation`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },

  /**
   * Approve a graduation request (admin/mentor)
   */
  async approveGraduation(cohortId: string, userId: string, notes?: string): Promise<void> {
    const response = await apiClient.put<ApiResponse<void>>(
      `/cohorts/${cohortId}/graduation/${userId}/approve`,
      { notes }
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
  },

  /**
   * Reject a graduation request (admin/mentor)
   */
  async rejectGraduation(cohortId: string, userId: string, notes?: string): Promise<void> {
    const response = await apiClient.put<ApiResponse<void>>(
      `/cohorts/${cohortId}/graduation/${userId}/reject`,
      { notes }
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
  },

  /**
   * Get role-aware action items for the current user in a cohort
   */
  async getActionItems(cohortId: string): Promise<CohortActionItems> {
    const response = await apiClient.get<ApiResponse<CohortActionItems>>(
      `/cohorts/${cohortId}/action-items`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },

  /**
   * Remove mentor
   */
  async removeMentor(cohortId: string, mentorId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/cohorts/${cohortId}/mentors/${mentorId}`
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Publish a cohort (one-way, idempotent)
   */
  async publish(cohortId: string): Promise<{ cohortId: string; isPublished: boolean }> {
    const response = await apiClient.post<ApiResponse<{ cohortId: string; isPublished: boolean }>>(`/cohorts/${cohortId}/publish`, {});
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },
};
